import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import Modal from '../../../../common/modal/Modal';
import TextInput from '../../../../common/inputs/text-input/TextInput';
import Dropdown from '../../../../common/inputs/dropdown/Dropdown';
import Button from '../../../../common/button/Button';

import './product-type-modal.scss';

const ProductTypeModal = ({ show, setShow, specification, retrieveCategories, createType, updateType, onHide }) => {
  const { classifications, categories, metricUnits } = useSelector(({ product }) => product);
  const { facilitators } = useSelector(({ party }) => party);

  const [classification, setClassification] = useState(undefined);
  const [category, setCategory] = useState(undefined);
  const [typeExternalReference, setTypeExternalReference] = useState(undefined);
  const [typeName, setTypeName] = useState('');
  const [metrics, setMetrics] = useState([]);
  const [facilitator, setFacilitator] = useState(undefined);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) {
      setClassification(undefined);
      setCategory(undefined);
      setTypeExternalReference(undefined);
      setTypeName('');
      setMetrics([]);
      setFacilitator(undefined);
      setErrors({});
      onHide();
    }
  }, [show, onHide]);

  useEffect(() => {
    if (specification) {
      setClassification(specification.classification);
      setCategory(specification.category);
      setTypeExternalReference(specification.type?.externalReference);
      setTypeName(specification.type?.name);

      const facilitator = facilitators.find(
        (facilitator) => facilitator.personName === specification.type.facilitatorName
      );

      if (facilitator) {
        setFacilitator(facilitator);
      }

      const metrics = specification.metricUnits?.map((metricUnit) => ({
        label: metricUnit,
        value: metricUnit,
      }));

      setMetrics(metrics);
      setErrors({});
    }
  }, [specification, facilitators]);

  const handleClassificationChange = (classification) => {
    setClassification(classification);
    setCategory(null);
    retrieveCategories(classification.externalReference);
  };

  const handleCategoryChange = (category) => {
    setCategory(category);
  };

  const validateFields = () => {
    const errors = {};

    if (!specification && !classification) errors.classification = true;
    if (!category) errors.category = true;
    if (!typeName) errors.type = true;
    if (metrics.length === 0) errors.metrics = true;
    if (!facilitator) errors.facilitator = true;

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = () => {
    const valid = validateFields();
    if (!valid) return;

    const specification = {
      classificationExternalReference: undefined,
      categoryExternalReference: category.externalReference,
      typeExternalReference: undefined,
      metricUnits: metrics.map((metric) => metric.value),
      facilitatorExternalReference: facilitator.personExternalReference,
      typeName,
    };

    if (!typeExternalReference) {
      specification.classificationExternalReference = classification.externalReference;
      createType(specification, () => setShow(false));
    } else {
      specification.typeExternalReference = typeExternalReference;
      updateType(specification, () => setShow(false));
    }
  };

  return (
    <Modal show={show} setShow={setShow}>
      <section className="product-type-modal">
        <h1>{specification ? 'Update Existing Product Type' : 'Create New Product Type'}</h1>

        {!specification && (
          <>
            <label htmlFor="classification">Product Classification</label>
            <Dropdown
              name="classification"
              value={classification}
              options={classifications}
              onChange={handleClassificationChange}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              error={errors.classification}
            />
          </>
        )}

        <label htmlFor="category">Product Category</label>
        <Dropdown
          name="category"
          value={category}
          options={categories}
          onChange={handleCategoryChange}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.externalReference}
          disabled={!classification}
          error={errors.category}
        />

        <label htmlFor="type">Product Type</label>
        <TextInput
          name="type"
          value={typeName}
          onChange={({ target }) => setTypeName(target.value)}
          disabled={!category}
          error={errors.type}
        />

        <label htmlFor="metrics">Metrics</label>
        <Dropdown
          name="metrics"
          value={metrics}
          options={metricUnits}
          onChange={(options) => setMetrics(options)}
          error={errors.metrics}
          multi
        />

        <label htmlFor="facilitator">Facilitator</label>
        <Dropdown
          name="facilitator"
          value={facilitator}
          options={facilitators}
          onChange={(option) => setFacilitator(option)}
          getOptionLabel={(option) => option.personName}
          getOptionValue={(option) => option.personExternalReference}
          error={errors.facilitator}
        />
        <div className="product-type-modal__buttons">
          <Button onClick={() => setShow(false)} secondary>
            Cancel
          </Button>
          <Button onClick={handleSaveClick} primary>
            Save
          </Button>
        </div>
      </section>
    </Modal>
  );
};

ProductTypeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  specification: PropTypes.shape({}),
  retrieveCategories: PropTypes.func.isRequired,
  createType: PropTypes.func.isRequired,
  updateType: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
};

ProductTypeModal.defaultProps = {
  specification: undefined,
};

export default ProductTypeModal;
